import { ChangeDetectionStrategy, Component, computed, effect, input, output, signal } from '@angular/core';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { TranslateModule } from '@ngx-translate/core';

import type { ExtendedPositionLegacy } from '@sites/entity-core';

import { SpInputNumComponent } from '../sp-form-controls/sp-input-num/sp-input-num.component';

type DeviceMode = 'desktop' | 'tablet' | 'mobile';
/** Axes with per-device variants stored as `<axis>` / `tablet<Axis>` / `mobile<Axis>`. */
type DimKey = 'x' | 'y' | 'width' | 'height' | 'rotate' | 'zIndex';

/**
 * Signal-form editor for the `ExtendedPositionLegacy` shape (defined in
 * `@sites/entity-core`). The signal-form analogue of the FormGroup-based
 * `web-widget-extended-position-settings` panel — mirrors that template's
 * layout (anchor point + alignment shortcuts + X/Y + arrangement +
 * z-index/rotate + width/height) but drives state through Angular signals.
 *
 * Contract:
 *
 *  - Parent passes a `computed()` snapshot of its signal-form position via
 *    the `position` input. Whenever the underlying form value changes
 *    (e.g. the user drags the widget on the zero canvas), the new value
 *    flows in and the local model resyncs via the `effect()` below.
 *  - Edits in this component update the local model and emit the next
 *    `ExtendedPositionLegacy` via `valueChangeEvent`. The parent then calls
 *    `form.setPosition(value)` so the signal form (and history dispatch)
 *    is the single source of truth.
 *
 * Per-axis device variants are stored as separate fields on the flat shape
 * (`width` / `tabletWidth` / `mobileWidth` etc.); the active device-mode
 * inputs (`isMobile`, `isTablet`) select which field the editors read/write.
 *
 * `parentId` is required for the alignment-shortcut buttons (left/center/right
 * for X, top/center/bottom for Y) — those compute the target X / Y from the
 * parent column's bounding rect minus the widget's rotated boundary box.
 */
@Component({
    selector: 'sp-extended-position-signal-settings',
    templateUrl: './sp-extended-position-signal-settings.component.html',
    styleUrls: ['./sp-extended-position-signal-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TooltipModule, TranslateModule, SpInputNumComponent],
})
export class SpExtendedPositionSignalSettingsComponent {
    /**
     * The parent passes a `computed()` snapshot of its signal-form position.
     * `null` is allowed for the brief window before the parent's form is
     * bootstrapped — the `effect` below no-ops on null and the template
     * reads through the local model (which is always populated).
     */
    public readonly position = input<ExtendedPositionLegacy | null>(null);
    public readonly isMobile = input<boolean>(false);
    public readonly isTablet = input<boolean>(false);
    /** Render width/height inputs only when the host widget can resize them. */
    public readonly showWidth = input<boolean>(true);
    public readonly showHeight = input<boolean>(true);
    /**
     * Mirror width↔height edits on the active device (square-ish widgets like
     * figure with "keep proportions" enabled). Mirrors the FormGroup panel's
     * input of the same name.
     */
    public readonly keepSizeProportions = input<boolean>(false);
    /**
     * DOM id of the parent column. Used by the alignment-shortcut buttons
     * to read the column's bounding rect and compute the target X/Y.
     */
    public readonly parentId = input<string>('');

    public readonly valueChangeEvent = output<ExtendedPositionLegacy>();

    private readonly model = signal<ExtendedPositionLegacy>(makeEmptyPosition());

    public readonly activeMode = computed<DeviceMode>(() => {
        if (this.isMobile()) return 'mobile';
        if (this.isTablet()) return 'tablet';
        return 'desktop';
    });

    // --- Active-mode value getters (template bindings) ---

    public readonly widthValue = computed(() => this.readAxis('width'));
    public readonly heightValue = computed(() => this.readAxis('height'));
    public readonly xValue = computed(() => this.readAxis('x'));
    public readonly yValue = computed(() => this.readAxis('y'));
    public readonly rotateValue = computed(() => this.readAxis('rotate'));
    public readonly zIndexValue = computed(() => this.readAxis('zIndex'));

    public readonly positionVertical = computed(() => this.model().positionVertical);
    public readonly positionHorizontal = computed(() => this.model().positionHorizontal);

    /**
     * Widget's on-screen footprint accounting for rotation — the bounding box
     * of the rotated rectangle. Used by the alignment shortcuts so a rotated
     * widget centers / right-aligns against its true visible bounds, not its
     * un-rotated width/height.
     */
    public readonly widgetBoundaryWidth = computed(
        () =>
            Math.abs(this.widthValue() * Math.cos(this.angle())) + Math.abs(this.heightValue() * Math.sin(this.angle()))
    );
    public readonly widgetBoundaryHeight = computed(
        () =>
            Math.abs(this.widthValue() * Math.sin(this.angle())) + Math.abs(this.heightValue() * Math.cos(this.angle()))
    );
    private readonly angle = computed(() => this.rotateValue() * (Math.PI / 180));

    constructor() {
        effect(() => {
            const incoming = this.position();
            if (!incoming) return;
            this.model.set(incoming);
        });
    }

    // --- Setters (template event handlers) ---

    public onXChange(value: number): void {
        this.writeAxisWithMobileTabletFlag('x', value);
    }
    public onYChange(value: number): void {
        this.writeAxisWithMobileTabletFlag('y', value);
    }
    public onWidthChange(value: number): void {
        if (this.keepSizeProportions()) {
            this.writeAxis('height', value, { emit: false });
        }
        this.writeAxis('width', value);
    }
    public onHeightChange(value: number): void {
        if (this.keepSizeProportions()) {
            this.writeAxis('width', value, { emit: false });
        }
        this.writeAxis('height', value);
    }
    public onRotateChange(value: number): void {
        this.writeAxis('rotate', value);
    }
    public onZIndexChange(value: number): void {
        this.writeAxis('zIndex', value);
    }

    public onPositionVerticalChange(value: string): void {
        this.model.update((m) => ({ ...m, positionVertical: value }));
        this.emit();
    }
    public onPositionHorizontalChange(value: string): void {
        this.model.update((m) => ({ ...m, positionHorizontal: value }));
        this.emit();
    }

    /**
     * Alignment shortcut — snaps the widget to the left / horizontal-center
     * / right of the parent column, accounting for the current `positionHorizontal`
     * anchor. When the anchor matches the requested side, X is set to 0
     * (flush against the side); otherwise X = parentWidth - widgetBoundaryWidth.
     * Center always sets X to half the leftover space.
     */
    public setWidgetHorizontalPosition(align: 'left' | 'center' | 'right'): void {
        const parent = this.parentId() ? document.getElementById(this.parentId()) : null;
        if (!parent) return;
        const rect = parent.getBoundingClientRect();
        const widgetBoundary = this.widgetBoundaryWidth();
        const anchor = this.positionHorizontal();

        switch (align) {
            case 'left':
                this.onXChange(anchor === 'left' ? 0 : rect.width - widgetBoundary);
                break;
            case 'center':
                this.onXChange((rect.width - widgetBoundary) / 2);
                break;
            case 'right':
                this.onXChange(anchor === 'right' ? 0 : rect.width - widgetBoundary);
                break;
        }
    }

    /**
     * Vertical sibling of {@link setWidgetHorizontalPosition} — snaps the
     * widget to the top / vertical-center / bottom of the parent column.
     */
    public setWidgetVerticalPosition(align: 'top' | 'center' | 'bottom'): void {
        const parent = this.parentId() ? document.getElementById(this.parentId()) : null;
        if (!parent) return;
        const rect = parent.getBoundingClientRect();
        const widgetBoundary = this.widgetBoundaryHeight();
        const anchor = this.positionVertical();

        switch (align) {
            case 'top':
                this.onYChange(anchor === 'top' ? 0 : rect.height - widgetBoundary);
                break;
            case 'center':
                this.onYChange((rect.height - widgetBoundary) / 2);
                break;
            case 'bottom':
                this.onYChange(anchor === 'bottom' ? 0 : rect.height - widgetBoundary);
                break;
        }
    }

    // --- Internal helpers ---

    /**
     * Maps `('width', 'tablet')` → `'tabletWidth'` (and 'desktop' → bare key).
     * Matches the field-name convention used by `webWidgetExtendedPositionSettingsFormGroup`.
     */
    private fieldName(axis: DimKey, mode: DeviceMode = this.activeMode()): string {
        if (mode === 'desktop') return axis;
        return mode + axis.charAt(0).toUpperCase() + axis.slice(1);
    }

    private readAxis(axis: DimKey, fallback = 0): number {
        const key = this.fieldName(axis);
        const value = (this.model() as Record<string, unknown>)[key];
        return typeof value === 'number' ? value : fallback;
    }

    private writeAxis(axis: DimKey, value: number, { emit = true }: { emit?: boolean } = {}): void {
        const key = this.fieldName(axis);
        this.model.update((m) => ({ ...m, [key]: value }));
        if (emit) {
            this.emit();
        }
    }

    /**
     * Same as {@link writeAxis} but also sets the per-device "position changed"
     * flag — used by the host renderer to know whether the user has customised
     * tablet/mobile placement away from the desktop default. Mirrors the
     * FormGroup component's `onXChange` / `onYChange` flag-setting behaviour.
     */
    private writeAxisWithMobileTabletFlag(axis: 'x' | 'y', value: number): void {
        const mode = this.activeMode();
        const key = this.fieldName(axis, mode);
        this.model.update((m) => {
            const next: ExtendedPositionLegacy = { ...m, [key]: value } as ExtendedPositionLegacy;
            if (mode === 'tablet') next.isTabletPositionChanged = true;
            if (mode === 'mobile') next.isMobilePositionChanged = true;
            return next;
        });
        this.emit();
    }

    private emit(): void {
        this.valueChangeEvent.emit(this.model());
    }
}

/**
 * Sentinel used only as the local model's initial value before the input
 * effect fires. Never emitted; only exists so `model()` is non-null in the
 * template before the parent feeds the first position snapshot.
 */
function makeEmptyPosition(): ExtendedPositionLegacy {
    return {
        width: 0,
        tabletWidth: 0,
        mobileWidth: 0,
        height: 0,
        tabletHeight: 0,
        mobileHeight: 0,
        x: 0,
        tabletX: 0,
        mobileX: 0,
        y: 0,
        tabletY: 0,
        mobileY: 0,
        isMobilePositionChanged: false,
        isTabletPositionChanged: false,
        rotate: 0,
        tabletRotate: 0,
        mobileRotate: 0,
        zIndex: 0,
        tabletZIndex: 0,
        mobileZIndex: 0,
        positionVertical: 'top',
        positionHorizontal: 'left',
    };
}
